import {Cookies} from "fearless-common-ui";
import { useEffect, useState } from 'react';

type PreferenceProps<V> = {
  key: string;
  reminderDays: number;
  defaultValue?: V;
} | string



/**
 * @param {string|undefined|{key: string, reminderDays: int}} propsPreference
 * @return {{preferenceValue: string, setPreferenceValue: Function }}
 */
export default function usePreference<Value extends object | string>(propsPreference: PreferenceProps<Value> | undefined) {
  const [preference, setPreferenceState] = useState<PreferenceProps<Value> | undefined>(propsPreference);
  const disabledPreference = typeof propsPreference === 'undefined' || !propsPreference;
  const hasReminder = typeof preference === 'object' && preference.reminderDays;

  const getKey = (preference: PreferenceProps<Value> | undefined) => (typeof preference === 'string' ? preference : (preference?.key || ''));

  const getValue = () => {
    const key = getKey(preference);
    // When the user should be reminded every X days, use cookies
    if (hasReminder) {
      // Encode objects in JSON
      const value = Cookies.get(key) || preference.defaultValue
      try {
        return (typeof value === 'string' ? JSON.parse(value): value) as Value;
      } catch (e) {
        return value as Value;
      }
    } else {
      // If the setting is fixed indeterminately, use local storage
      const value = localStorage.getItem(key);

      // If the value was not actively disabled, show if by the default
      if (value === null && typeof preference === 'object') {
        return preference.defaultValue as Value;
      } else {
        return value as Value;
      }
    }
  };

  const [preferenceValue, setValue] = useState<undefined | Value>(getValue());

  useEffect(() => {
    if (!disabledPreference) setPreferenceState(propsPreference);
  }, [propsPreference]);


  useEffect(() => {
    if (!disabledPreference) {
      setValue(getValue());
    }
  }, []);

  const setPreferenceValue = (value: Value | undefined) => {
    if (!disabledPreference) {
      const key = getKey(preference);
      if (hasReminder) {
        // When the preference has should not last indeterminately, cookies are used
        if (!value) {
          Cookies.remove(key);
        } else {
          const encodedValue: string = typeof value === 'object' ? JSON.stringify(value) : value;
          Cookies.set(key, encodedValue, preference.reminderDays);
        }
      } else {
        // If the setting is fixed indeterminately, use local storage
        if (value === '' || value === undefined) {
          localStorage.removeItem(key);
        } else {
          const encodedValue: string = typeof value === 'object' ? JSON.stringify(value) : value;
          localStorage.setItem(key, encodedValue);
        }
      }
      setValue(value);
    }
  };

  return { setPreferenceValue, preferenceValue };
}
